import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
  inject,
  forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ChemicalService, Chemical } from '../../../core/services/chemical.service';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-chemical-typeahead',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MaxLengthDirective],
  templateUrl: './chemical-typeahead.component.html',
  styleUrls: ['./chemical-typeahead.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChemicalTypeaheadComponent),
      multi: true
    }
  ]
})
export class ChemicalTypeaheadComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder = 'Search chemicals...';
  @Input() disabled = false;
  @Input() showCategory = true;
  @Input() showEpaNumber = true;

  @Output() chemicalSelected = new EventEmitter<Chemical>();
  @Output() cleared = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') dropdown!: ElementRef<HTMLDivElement>;

  private chemicalService = inject(ChemicalService);

  searchQuery = '';
  results: Chemical[] = [];
  isOpen = false;
  isLoading = false;
  highlightedIndex = -1;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // ControlValueAccessor - selectedChemical is public for template access
  selectedChemical: Chemical | null = null;
  private onChange: (value: Chemical | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: Chemical | null): void {
    this.selectedChemical = value;
    if (value) {
      this.searchQuery = value.name;
    } else {
      this.searchQuery = '';
    }
  }

  registerOnChange(fn: (value: Chemical | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;

    // Clear selection when user types
    if (this.selectedChemical && this.searchQuery !== this.selectedChemical.name) {
      this.selectedChemical = null;
      this.onChange(null);
    }

    this.searchSubject.next(this.searchQuery);
  }

  private async performSearch(query: string): Promise<void> {
    if (!query || query.length < 2) {
      this.results = [];
      this.isOpen = false;
      return;
    }

    this.isLoading = true;
    try {
      const searchResult = await this.chemicalService.searchChemicals(query, { limit: 15 });
      this.results = searchResult.chemicals;
      this.isOpen = this.results.length > 0;
      this.highlightedIndex = -1;
    } catch (error) {
      console.error('Chemical search error:', error);
      this.results = [];
      this.isOpen = false;
    } finally {
      this.isLoading = false;
    }
  }

  onFocus(): void {
    this.onTouched();
    if (this.searchQuery.length >= 2 && this.results.length > 0) {
      this.isOpen = true;
    }
  }

  onBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      if (!this.selectedChemical && this.searchQuery) {
        // Clear if no selection was made
        this.searchQuery = '';
      }
      this.isOpen = false;
    }, 200);
  }

  selectChemical(chemical: Chemical): void {
    this.selectedChemical = chemical;
    // Use displayName if available (custom name), otherwise use EPA name
    this.searchQuery = chemical.displayName || chemical.name;
    this.isOpen = false;
    this.results = [];
    this.onChange(chemical);
    this.chemicalSelected.emit(chemical);
  }

  clearSelection(): void {
    this.selectedChemical = null;
    this.searchQuery = '';
    this.results = [];
    this.isOpen = false;
    this.onChange(null);
    this.cleared.emit();
    this.searchInput?.nativeElement?.focus();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.results.length - 1);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.results.length) {
          this.selectChemical(this.results[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        break;
    }
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      const highlighted = this.dropdown?.nativeElement?.querySelector('.dropdown-item.active');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.searchInput?.nativeElement?.contains(target) &&
        !this.dropdown?.nativeElement?.contains(target)) {
      this.isOpen = false;
    }
  }

  getCategoryBadgeClass(category: string): string {
    return this.chemicalService.getCategoryBadgeClass(category);
  }

  getSourceBadgeClass(source: string): string {
    return this.chemicalService.getSourceBadgeClass(source);
  }

  getSourceLabel(source: string): string {
    return this.chemicalService.getSourceLabel(source);
  }

  get hasSelection(): boolean {
    return !!this.selectedChemical;
  }

  /**
   * Check if the search query matches an alternate brand name
   * (but not the main name or display name)
   */
  hasAltNameMatch(chemical: Chemical): boolean {
    if (!this.searchQuery || this.searchQuery.length < 2) return false;
    if (!chemical.epaAltBrandNames) return false;

    const query = this.searchQuery.toLowerCase();

    // Check if query already matches name or displayName
    const matchesName = chemical.name?.toLowerCase().includes(query);
    const matchesDisplayName = chemical.displayName?.toLowerCase().includes(query);

    if (matchesName || matchesDisplayName) return false;

    // Check if query matches alternate brand names
    return chemical.epaAltBrandNames.toLowerCase().includes(query);
  }

  /**
   * Get the matching alternate name from the semicolon-delimited list
   */
  getMatchingAltName(chemical: Chemical): string {
    if (!chemical.epaAltBrandNames || !this.searchQuery) return '';

    const query = this.searchQuery.toLowerCase();
    const altNames = chemical.epaAltBrandNames.split(';').map(n => n.trim());

    // Find the name that matches the query
    const match = altNames.find(name => name.toLowerCase().includes(query));
    return match || altNames[0] || '';
  }
}
